import React, { useEffect, useState } from "react";
import { FaArrowLeftLong, FaArrowRightLong } from "react-icons/fa6";
import { FaSearch } from "react-icons/fa";
import { BounceLoader } from "react-spinners";
import axios from "axios";
import toast from "react-hot-toast";
import moment from "moment";
import parse from "html-react-parser";
import { apiUrl } from "../../common/config/apiUrl";
import { useNavigate } from "react-router-dom";
import HeroSection from "./HeroSection";

const BlogList = () => {
  const [categories, setCategories] = useState([]);
  const [posts, setPosts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchInput, setSearchInput] = useState(""); 
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingPosts, setLoadingPosts] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [next, setNext] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    if(selectedCategory > 0 ){
      setSearchQuery("")
    }
    fetchPosts(currentPage, selectedCategory, searchQuery);
  }, [currentPage, selectedCategory, searchQuery]);

  const fetchCategories = async () => {
    setLoadingCategories(true);
    try {
      const response = await axios.get(`${apiUrl}post/categoryall`);
      setCategories(response?.data?.data?.categories || []);
    } catch (error) {
      toast.error("Failed to load categories.");
    } finally {
      setLoadingCategories(false);
    }
  };

  const fetchPosts = async (page, categoryId, query) => {
    setLoadingPosts(true);
    try {
      let url = `${apiUrl}post/?page=${page}`;
      if (categoryId) url += `&category_id=${categoryId}`;
      if (query) url += `&search=${query}`;

      const response = await axios.get(url);
      setNext(response?.data?.data?.next);
      setPosts(response?.data?.data?.posts || []);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to load posts.");
    } finally {
      setLoadingPosts(false);
    }
  };

  const handleSearch = () => {
    setSearchQuery(searchInput);
  };
  return (
    <>
      <HeroSection />
      <div className="max-w-[1280px] mx-auto p-5 grid small:grid-cols-3 grid-cols-1 md:gap-10 gap-y-5 small:gap-y-0">
        {/* Search Bar */}
        <div className="bg-white small:hidden block shadow-md rounded-md mb-5">
          <p className="bg-[#FEC952] h-[7px] w-[53px] ml-4"></p>
          <div className="p-4">
            <h3 className="text-xl text-secondary font-medium pb-2">SEARCH</h3>
            <div className="flex items-center border rounded-full border-[#FEC952] px-3 py-2">
              <input
                type="text"
                placeholder="Search Blog Here"
                className="outline-none w-full"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
              <button onClick={handleSearch}>
                <FaSearch
                  className={`cursor-pointer transition-transform ${
                    searchInput?.length > 0
                      ? "text-secondary scale-110 animate-pulse"
                      : "text-gray-500"
                  }`}
                />
              </button>
            </div>
          </div>
        </div>

        {/* Blog Posts */}
        <div className="col-span-2">
          <h2 className="text-2xl font-bold mb-4 border-b-2 pb-2">Blogs</h2>

          {loadingPosts ? (
            <div className="flex justify-center my-10">
              <BounceLoader color="#FFC400" size={50} />
            </div>
          ) : (
            posts.map((post) => (
              <div key={post.id} className="mb-6 bg-white shadow rounded-lg">
                <div className="p-4">
                  <img
                    src={post?.image}
                    alt={post.title}
                    className="w-full object-cover h-45 rounded"
                  />
                  <p className="text-sm text-gray-500 mt-2">
                    {post?.category?.title} • by Binary Ops •{" "}
                    {moment(post?.created_at).format("MMM DD, YYYY")}
                  </p>
                  <div className="w-fit">
                    <h3 className="text-xl font-semibold mt-2">
                      {post?.title}
                    </h3>
                    <p className="h-[3px] w-full bg-[#FFC400] rounded-lg my-2"></p>
                  </div>
                  <p className="text-gray-600 mt-2 line-clamp-5 para_index">
                    {parse(post?.body || "")}
                  </p>
                  <div className="flex mt-4 gap-2 justify-between items-center">
                    <button
                      className="px-4 py-2 bg-white text-black border text-sm small:text-md border-primary rounded-full flex items-center gap-2 hover:bg-yellow-400 transition"
                      onClick={() => navigate(`/blogs/${post.slug}`)}
                    >
                      Read More{" "}
                      <FaArrowRightLong className="small:text-lg text-sm" />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}

          {/* Pagination */}
          <div className="flex justify-between w-full max-w-sm mx-auto">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className={`flex items-center gap-2 px-4 py-2 rounded-full transition ${
                currentPage === 1
                  ? "bg-gray-400 text-white cursor-not-allowed"
                  : "bg-blue-800 text-white hover:bg-blue-900"
              }`}
            >
              <FaArrowLeftLong className="text-lg" />
              OLDER POST
            </button>

            <button
              onClick={() => setCurrentPage((prev) => prev + 1)}
              disabled={!next}
              className={`flex items-center gap-2 px-4 py-2 rounded-full transition ${
                !next
                  ? "bg-gray-400 text-white cursor-not-allowed"
                  : "bg-yellow-400 text-black hover:bg-yellow-700"
              }`}
            >
              NEWER POST
              <FaArrowRightLong className="text-lg" />
            </button>
          </div>
        </div>

        {/* Sidebar */}
        <div className="col-span-1">
        <div className="bg-white small:block hidden shadow-md rounded-md mb-5">
          <p className="bg-[#FEC952] h-[7px] w-[53px] ml-4"></p>
          <div className="p-4">
            <h3 className="text-xl text-secondary font-medium pb-2">SEARCH</h3>
            <div className="flex items-center border rounded-full border-[#FEC952] px-3 py-2">
              <input
                type="text"
                placeholder="Search Blog Here"
                className="outline-none w-full"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
              <button onClick={handleSearch}>
                <FaSearch
                  className={`cursor-pointer transition-transform ${
                    searchInput?.length > 0
                      ? "text-secondary scale-110 animate-pulse"
                      : "text-gray-500"
                  }`}
                />
              </button>
            </div>
          </div>
        </div>

          {/* Categories */}
          <div className="bg-white shadow-md rounded-md mb-5">
            <p className="bg-[#FEC952] h-[7px] w-[53px] ml-4"></p>
            <div className="p-4">
              <h3 className="text-xl text-secondary font-medium pb-2">
                Categories
              </h3>
              {loadingCategories ? (
                <div className="flex justify-center my-4">
                  <BounceLoader color="#FFC400" size={30} />
                </div>
              ) : (
                <ul className="text-gray-700">
                  {categories.map((category) => (
                    <li
                      key={category.id}
                      className={`my-2 p-2 border-b border-[#FFC400] cursor-pointer ${
                        selectedCategory === category.id
                          ? "bg-secondary rounded-md text-white"
                          : ""
                      }`}
                      onClick={() => setSelectedCategory(category.id)}
                    >
                      {category.title}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default BlogList;
